import { debounce } from "lodash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  ActionMeta,
  GroupBase,
  InputActionMeta,
  OnChangeValue
} from "react-select";
import CreatableSelect, { CreatableProps } from "react-select/creatable";
import Select from "react-select/base";
import { MenuItem } from "../../@types";
import RenderWhen from "../../RenderWhen";
import FormElementErrorMessage from "../errormessage/FormElementErrorMessage";

const DEFAULT_MENU_PAGE_SIZE = 50;
const INPUT_CHANGE_DEBOUNCE = 500;
const LOAD_MORE_KEY = "__more__";

function addLoadMoreOptionsIfNecessary<T>(options: T[], pageSize: number): T[] {
  if (options.length > pageSize) {
    return [
      ...options.slice(0, pageSize),
      { label: "Load More...", value: LOAD_MORE_KEY } as T
    ];
  }

  return options;
}

type ISelectWithPaging<
  Option extends MenuItem,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = CreatableProps<Option, IsMulti, Group> & {
  menuPageSize?: number;
  menuPageSizeIncrement?: number;
  inputChangeDebounce?: number;
  menuOptions?: Option[] | ((inputValue: string) => Option[]);
  errorMsg?: string;
  touched?: boolean;
};

/**
 * A wrapper around react-select that adds infinite scrolling to the menu.
 * This is useful when the number of options is very large.
 * @param props The props to pass to react-select. Use the `menuOptions` prop to
 * provide a function that will return the options or a value to display in the menu.
 * Don't use the `options` prop because it will be ignored.
 * @returns A react-select component with infinite scrolling.
 */
export default function SelectWithPaging<
  Option extends MenuItem,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(props: ISelectWithPaging<Option, IsMulti, Group>) {
  const {
    menuPageSize = DEFAULT_MENU_PAGE_SIZE,
    menuPageSizeIncrement = DEFAULT_MENU_PAGE_SIZE,
    inputChangeDebounce = INPUT_CHANGE_DEBOUNCE,
    isValidNewOption: isValidNewOptionProp,
    isLoading: isLoadingProp,
    menuOptions: optionsProp,
    onMenuClose: onMenuCloseProp,
    onMenuOpen: onMenuOpenProp,
    onInputChange: onInputChangeProp,
    onMoreResultsClick: onMoreResultsClickProp,
    onChange: onChangeProp,
    value: valueProp,
    errorMsg,
    touched
  } = props;

  const ref = useRef<Select<Option, IsMulti, Group>>(null);
  const [inputValue, setInputValue] = useState("");
  const [shownInputValue, setShownInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [menuSize, setMenuSize] = useState(menuPageSize);

  const menuIsLoading = isLoading || isLoadingProp;
  const valueSet = useMemo(
    () => new Set((valueProp as Option[]).map(v => v.value)),
    [valueProp]
  );
  const allOptions = useMemo(() => {
    const initialOptions =
      typeof optionsProp === "function" ? optionsProp("") : optionsProp;

    const unselectedOptions =
      initialOptions?.filter(value => !valueSet.has(value.value)) || [];

    if (!inputValue) return unselectedOptions;

    return unselectedOptions.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, optionsProp, valueSet]);

  const visibleOptions = useMemo(
    () => addLoadMoreOptionsIfNecessary(allOptions ?? [], menuSize),
    [allOptions, menuSize]
  );

  const inputChangedFunction = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      setMenuSize(menuPageSize);
      onInputChangeProp?.(newValue, actionMeta);
      setInputValue(newValue);
      setIsLoading(false);
    },
    [menuPageSize, onInputChangeProp]
  );

  const debouncedInputChange = useMemo(
    () => debounce(inputChangedFunction, inputChangeDebounce),
    [inputChangeDebounce, inputChangedFunction]
  );

  const onMoreResultsClick = () => {
    if (menuIsLoading) return;
    ref.current?.setState({
      focusedOption:
        allOptions?.[Math.min(menuSize, allOptions.length - 1)] ?? null
    });
    setMenuSize(prevState => prevState + menuPageSizeIncrement);
    onMoreResultsClickProp?.();
  };

  const onInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    if (
      actionMeta.action === "set-value" &&
      ref.current?.state.focusedOption?.value === LOAD_MORE_KEY
    )
      return;

    setIsLoading(true);
    setShownInputValue(newValue);
    debouncedInputChange(newValue, actionMeta);
  };

  const onMenuClose = () => {
    setIsLoading(false);
    setMenuSize(menuPageSize);
    onMenuCloseProp?.();
  };

  const onMenuOpen = () => {
    setMenuSize(menuPageSize);
    onMenuOpenProp?.();
  };

  const onChange = (
    newValue: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (
      actionMeta.action === "select-option" &&
      actionMeta.option?.value === LOAD_MORE_KEY
    ) {
      onMoreResultsClick();
      return;
    }

    setMenuSize(menuPageSize);
    setIsLoading(false);
    setInputValue("");
    onChangeProp?.(newValue, actionMeta);
  };

  return (
    <>
      <CreatableSelect<Option, IsMulti, Group>
        {...props}
        ref={ref}
        inputValue={shownInputValue}
        isValidNewOption={isValidNewOptionProp || (() => false)}
        isLoading={menuIsLoading}
        options={menuIsLoading ? [] : visibleOptions}
        totalResults={optionsProp?.length}
        onInputChange={onInputChange}
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        closeMenuOnSelect={false}
        onChange={onChange}
      />
      <RenderWhen condition={!!errorMsg && !touched}>
        <FormElementErrorMessage message={errorMsg} />
      </RenderWhen>
    </>
  );
}
